import { createIntl } from 'react-intl-next';

import { ConditionFilterMessages } from './messages';
import { type IFieldOptions, type ISelectOption, type MATCH_TYPE } from './types';

const { formatMessage } = createIntl({ locale: 'en' });

export const OPERATIONS = {
	'matches-regex': {
		value: 'matches',
		label: formatMessage(ConditionFilterMessages.OperationRegex),
	},
	'not-matches-regex': {
		value: 'not-matches',
		label: formatMessage(ConditionFilterMessages.NotOperationRegex),
	},
	matches: {
		value: 'matches',
		label: formatMessage(ConditionFilterMessages.OperationRegex),
	},
	'not-matches': {
		value: 'not-matches',
		label: formatMessage(ConditionFilterMessages.NotOperationRegex),
	},
	contains: {
		value: 'contains',
		label: formatMessage(ConditionFilterMessages.OperationContains),
	},
	'not-contains': {
		value: 'not-contains',
		label: formatMessage(ConditionFilterMessages.NotOperationContains),
	},
	'contains-key': {
		value: 'contains-key',
		label: formatMessage(ConditionFilterMessages.OperationContainsKey),
	},
	'not-contains-key': {
		value: 'not-contains-key',
		label: formatMessage(ConditionFilterMessages.NotOperationContainsKey),
	},
	'contains-value': {
		value: 'contains-value',
		label: formatMessage(ConditionFilterMessages.OperationContainsValue),
	},
	'not-contains-value': {
		value: 'not-contains-value',
		label: formatMessage(ConditionFilterMessages.NotOperationContainsValue),
	},
	'starts-with': {
		value: 'starts-with',
		label: formatMessage(ConditionFilterMessages.OperationStartsWith),
	},
	'not-starts-with': {
		value: 'not-starts-with',
		label: formatMessage(ConditionFilterMessages.NotOperationStartsWith),
	},
	'ends-with': {
		value: 'ends-with',
		label: formatMessage(ConditionFilterMessages.OperationEndsWith),
	},
	'not-ends-with': {
		value: 'not-ends-with',
		label: formatMessage(ConditionFilterMessages.NotOperationEndsWith),
	},
	equals: {
		value: 'equals',
		label: formatMessage(ConditionFilterMessages.OperationEquals),
	},
	'not-equals': {
		value: 'not-equals',
		label: formatMessage(ConditionFilterMessages.NotOperationEquals),
	},
	'is-empty': {
		value: 'is-empty',
		label: formatMessage(ConditionFilterMessages.OperationIsEmpty),
	},
	'not-is-empty': {
		value: 'not-is-empty',
		label: formatMessage(ConditionFilterMessages.NotOperationIsEmpty),
	},
	'equals-ignore-whitespace': {
		value: 'equals-ignore-whitespace',
		label: formatMessage(ConditionFilterMessages.OperationEqualsIgnoreWhitespace),
	},
	'not-equals-ignore-whitespace': {
		value: 'not-equals-ignore-whitespace',
		label: formatMessage(ConditionFilterMessages.NotOperationEqualsIgnoreWhitespace),
	},
	'greater-than': {
		value: 'greater-than',
		label: formatMessage(ConditionFilterMessages.OperationGreaterThan),
	},
	'not-greater-than': {
		value: 'not-greater-than',
		label: formatMessage(ConditionFilterMessages.NotOperationGreaterThan),
	},
	'less-than': {
		value: 'less-than',
		label: formatMessage(ConditionFilterMessages.OperationLessThan),
	},
	'not-less-than': {
		value: 'not-less-than',
		label: formatMessage(ConditionFilterMessages.NotOperationLessThan),
	},
} as const;

export const FIELD_TYPES = [
	'string',
	'list',
	'keyValue',
	'boolean',
	'number',
	'priority',
	'map',
	'url',
] as const;

export const FIELD_MAPPINGS = {
	string: {
		operations: [
			OPERATIONS['matches-regex'],
			OPERATIONS['not-matches-regex'],
			OPERATIONS['contains'],
			OPERATIONS['not-contains'],
			OPERATIONS['starts-with'],
			OPERATIONS['not-starts-with'],
			OPERATIONS['ends-with'],
			OPERATIONS['not-ends-with'],
			OPERATIONS['equals'],
			OPERATIONS['not-equals'],
			OPERATIONS['is-empty'],
			OPERATIONS['not-is-empty'],
			OPERATIONS['equals-ignore-whitespace'],
			OPERATIONS['not-equals-ignore-whitespace'],
		],
		default: OPERATIONS['contains'],
	},
	list: {
		operations: [
			OPERATIONS['matches-regex'],
			OPERATIONS['not-matches-regex'],
			OPERATIONS['contains'],
			OPERATIONS['not-contains'],
			OPERATIONS['is-empty'],
			OPERATIONS['not-is-empty'],
		],
		default: OPERATIONS['contains'],
	},
	keyValue: {
		operations: [
			OPERATIONS['matches-regex'],
			OPERATIONS['not-matches-regex'],
			OPERATIONS['contains'],
			OPERATIONS['not-contains'],
			OPERATIONS['starts-with'],
			OPERATIONS['not-starts-with'],
			OPERATIONS['ends-with'],
			OPERATIONS['not-ends-with'],
			OPERATIONS['equals'],
			OPERATIONS['not-equals'],
			OPERATIONS['is-empty'],
			OPERATIONS['not-is-empty'],
			OPERATIONS['equals-ignore-whitespace'],
			OPERATIONS['not-equals-ignore-whitespace'],
		],
		default: OPERATIONS['contains'],
	},
	boolean: {
		operations: [OPERATIONS['equals'], OPERATIONS['not-equals']],
		default: OPERATIONS['equals'],
	},
	number: {
		operations: [
			OPERATIONS['matches-regex'],
			OPERATIONS['not-matches-regex'],
			OPERATIONS['equals'],
			OPERATIONS['not-equals'],
			OPERATIONS['greater-than'],
			OPERATIONS['not-greater-than'],
			OPERATIONS['less-than'],
			OPERATIONS['not-less-than'],
		],
		default: OPERATIONS['matches-regex'],
	},
	priority: {
		operations: [
			OPERATIONS['equals'],
			OPERATIONS['not-equals'],
			OPERATIONS['greater-than'],
			OPERATIONS['not-greater-than'],
			OPERATIONS['less-than'],
			OPERATIONS['not-less-than'],
		],
		default: OPERATIONS['equals'],
	},
	map: {
		operations: [
			OPERATIONS['contains'],
			OPERATIONS['not-contains'],
			OPERATIONS['contains-key'],
			OPERATIONS['not-contains-key'],
			OPERATIONS['contains-value'],
			OPERATIONS['not-contains-value'],
			OPERATIONS['is-empty'],
			OPERATIONS['not-is-empty'],
		],
		default: OPERATIONS['contains'],
	},
	url: {
		operations: [
			OPERATIONS['matches-regex'],
			OPERATIONS['not-matches-regex'],
			OPERATIONS['contains'],
			OPERATIONS['not-contains'],
			OPERATIONS['starts-with'],
			OPERATIONS['not-starts-with'],
			OPERATIONS['ends-with'],
			OPERATIONS['not-ends-with'],
			OPERATIONS['equals'],
			OPERATIONS['not-equals'],
			OPERATIONS['is-empty'],
			OPERATIONS['not-is-empty'],
		],
		default: OPERATIONS['contains'],
	},
} as const;

export const FIELD_MAPPINGS_OLD = {
	string: {
		operations: [
			OPERATIONS['matches-regex'],
			OPERATIONS['contains'],
			OPERATIONS['starts-with'],
			OPERATIONS['ends-with'],
			OPERATIONS['equals'],
			OPERATIONS['is-empty'],
			OPERATIONS['equals-ignore-whitespace'],
		],

		default: OPERATIONS['contains'],
	},
	list: {
		operations: [OPERATIONS['matches-regex'], OPERATIONS['contains'], OPERATIONS['is-empty']],
		default: OPERATIONS['contains'],
	},
	keyValue: {
		operations: [
			OPERATIONS['matches-regex'],
			OPERATIONS['contains'],
			OPERATIONS['starts-with'],
			OPERATIONS['ends-with'],
			OPERATIONS['equals'],
			OPERATIONS['is-empty'],
			OPERATIONS['equals-ignore-whitespace'],
		],
		default: OPERATIONS['contains'],
	},
	boolean: {
		operations: [OPERATIONS['equals']],
		default: OPERATIONS['equals'],
	},
	number: {
		operations: [
			OPERATIONS['matches-regex'],
			OPERATIONS['equals'],
			OPERATIONS['greater-than'],
			OPERATIONS['less-than'],
		],
		default: OPERATIONS['matches-regex'],
	},
	priority: {
		operations: [OPERATIONS['equals'], OPERATIONS['greater-than'], OPERATIONS['less-than']],
		default: OPERATIONS['equals'],
	},
	map: {
		operations: [
			OPERATIONS['contains'],
			OPERATIONS['contains-key'],
			OPERATIONS['contains-value'],
			OPERATIONS['is-empty'],
		],
		default: OPERATIONS['contains'],
	},
	url: {
		operations: [
			OPERATIONS['matches-regex'],
			OPERATIONS['contains'],
			OPERATIONS['starts-with'],
			OPERATIONS['ends-with'],
			OPERATIONS['equals'],
			OPERATIONS['is-empty'],
		],
		default: OPERATIONS['contains'],
	},
} as const;

// constant values for dropdown
export const DEFAULT_FIELDS: IFieldOptions[] = [
	{
		label: formatMessage(ConditionFilterMessages.FieldKeyValue),
		value: '',
		type: 'keyValue',
	},
	{
		label: formatMessage(ConditionFilterMessages.FieldMessage),
		value: 'message',
		type: 'string',
	},
	{
		label: formatMessage(ConditionFilterMessages.FieldResponders),
		value: 'responders',
		type: 'list',
	},
	{
		label: formatMessage(ConditionFilterMessages.FieldAlias),
		value: 'alias',
		type: 'string',
	},
	{
		label: formatMessage(ConditionFilterMessages.FieldEntity),
		value: 'entity',
		type: 'string',
	},
	{
		label: formatMessage(ConditionFilterMessages.FieldSource),
		value: 'source',
		type: 'string',
	},
	{
		label: formatMessage(ConditionFilterMessages.FieldTags),
		value: 'tags',
		type: 'list',
	},
	{
		label: formatMessage(ConditionFilterMessages.FieldActions),
		value: 'actions',
		type: 'list',
	},
	{
		label: formatMessage(ConditionFilterMessages.FieldDescription),
		value: 'description',
		type: 'string',
	},
	{
		label: formatMessage(ConditionFilterMessages.FieldDetails),
		value: 'extraProperties',
		type: 'map',
	},
	{
		label: formatMessage(ConditionFilterMessages.FieldPriority),
		value: 'priority',
		type: 'priority',
	},
];

export const BOOLEAN_FIELD: ISelectOption<boolean>[] = [
	{ label: '--', value: false },
	{ label: formatMessage(ConditionFilterMessages.BooleanNot), value: true },
];

export const CONDITION_MATCH_TYPES_OLD: ISelectOption<MATCH_TYPE>[] = [
	{
		value: 'match-all',
		label: formatMessage(ConditionFilterMessages.MatchAll),
	},
	{
		value: 'match-any-condition',
		label: formatMessage(ConditionFilterMessages.MatchAnyCondition),
	},
	{
		value: 'match-all-conditions',
		label: formatMessage(ConditionFilterMessages.MatchAllConditions),
	},
];

export const CONDITION_MATCH_TYPES: ISelectOption<MATCH_TYPE>[] = [
	{
		value: 'match-all',
		label: formatMessage(ConditionFilterMessages.MatchAll),
	},
	{
		value: 'match-any-condition',
		label: formatMessage(ConditionFilterMessages.MatchAnyConditionOption),
	},
	{
		value: 'match-all-conditions',
		label: formatMessage(ConditionFilterMessages.MatchAllConditionsOption),
	},
];

export const CUSTOM_DYNAMIC_PROPERTY_SYNTAX_EXAMPLE = '{{_payload.customField}}';
export const CUSTOM_DYNAMIC_PROPERTY_SYNTAX_EXAMPLE_2 = '{{_headers.customField}}';
export const CUSTOM_DYNAMIC_PROPERTY_SYNTAX_EXAMPLE_3 = '{{_url.customField}}';
